import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { BookmarkSimple, Trash, CaretDown, CaretUp, Plus } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export const TemplateManager = ({ templateType, currentValues, onLoad }) => {
  const { api } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [templateDesc, setTemplateDesc] = useState("");

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get(`/templates?template_type=${templateType}`);
      setTemplates(Array.isArray(resp.data) ? resp.data : resp.data.templates || []);
    } catch (e) {
      console.error("Failed to fetch templates:", e);
    } finally {
      setLoading(false);
    }
  }, [api, templateType]);

  useEffect(() => {
    if (expanded) fetchTemplates();
  }, [expanded, fetchTemplates]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!templateName.trim()) {
      toast.error("Give your template a name");
      return;
    }
    setSaving(true);
    try {
      await api.post("/templates", {
        name: templateName.trim(),
        description: templateDesc.trim(),
        template_type: templateType,
        ...currentValues,
      });
      toast.success("Template saved");
      setTemplateName("");
      setTemplateDesc("");
      setShowSaveForm(false);
      fetchTemplates();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save template");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (templateId, e) => {
    e.stopPropagation();
    try {
      await api.delete(`/templates/${templateId}`);
      setTemplates((prev) => prev.filter((t) => t.template_id !== templateId));
      toast.success("Template deleted");
    } catch {
      toast.error("Failed to delete template");
    }
  };

  const handleLoad = async (template) => {
    try {
      await api.get(`/templates/${template.template_id}`);
    } catch { /* silent */ }
    onLoad(template);
    setExpanded(false);
    toast.success(`Loaded "${template.name}"`);
  };

  return (
    <div className="bg-[var(--bg-base)] border border-[var(--border)] rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-[var(--bg-hover)] transition-colors"
        data-testid="template-manager-toggle"
      >
        <div className="flex items-center gap-2">
          <BookmarkSimple size={16} className="text-[#FF4400]" />
          <span className="text-sm font-medium text-[var(--text-secondary)]">Templates</span>
          {templates.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-xs bg-[var(--accent-subtle)] text-[#FF4400] font-['JetBrains_Mono']">
              {templates.length}
            </span>
          )}
        </div>
        {expanded ? (
          <CaretUp size={16} className="text-[var(--text-muted)]" />
        ) : (
          <CaretDown size={16} className="text-[var(--text-muted)]" />
        )}
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden border-t border-[var(--border)]"
          >
            <div className="p-4 space-y-4">
              {!showSaveForm ? (
                <button
                  type="button"
                  onClick={() => setShowSaveForm(true)}
                  className="flex items-center gap-2 text-sm text-[var(--text-secondary)] hover:text-[#FF4400] transition-colors"
                  data-testid="save-template-trigger"
                >
                  <Plus size={14} />
                  Save current form as template
                </button>
              ) : (
                <div className="space-y-2 pb-3 border-b border-[var(--border)]">
                  <input
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="Template name (e.g., LinkedIn Authority Post)"
                    maxLength={80}
                    className="w-full bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                    data-testid="template-name-input"
                    autoFocus
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(e); } }}
                  />
                  <input
                    value={templateDesc}
                    onChange={(e) => setTemplateDesc(e.target.value)}
                    placeholder="Description (optional)"
                    maxLength={200}
                    className="w-full bg-[var(--bg-card)] border border-[var(--border-strong)] rounded-md px-3 py-2 text-sm text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:outline-none focus:ring-1 focus:ring-[#FF4400]"
                    data-testid="template-desc-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(e); } }}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleSave}
                      disabled={saving}
                      className="px-3 py-1.5 text-xs bg-[#FF4400] text-white rounded-md hover:bg-[#FF6633] transition-colors disabled:opacity-50"
                      data-testid="save-template-submit"
                    >
                      {saving ? "Saving..." : "Save Template"}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowSaveForm(false); setTemplateName(""); setTemplateDesc(""); }}
                      className="px-3 py-1.5 text-xs bg-[var(--bg-raised)] text-[var(--text-secondary)] rounded-md border border-[var(--border-strong)] hover:text-[var(--text-primary)] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => (
                    <div key={i} className="h-14 bg-[var(--bg-card)] rounded-md animate-pulse" />
                  ))}
                </div>
              ) : templates.length === 0 ? (
                <p className="text-sm text-[var(--text-muted)]" data-testid="no-templates-message">No templates saved yet. Fill out the form and save it as a template to reuse it.</p>
              ) : (
                <div className="space-y-2">
                  {templates.map((template) => (
                    <div
                      key={template.template_id}
                      className="flex items-center justify-between px-3 py-2.5 rounded-md bg-[var(--bg-card)] border border-[var(--border)] hover:border-[#FF4400]/30 cursor-pointer group transition-colors"
                      onClick={() => handleLoad(template)}
                      data-testid={`template-item-${template.template_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-[var(--text-primary)] font-medium truncate">{template.name}</p>
                        <div className="flex items-center gap-2 mt-0.5">
                          {template.content_type && (
                            <span className="text-[10px] text-[var(--text-muted)] font-['JetBrains_Mono']">{template.content_type}</span>
                          )}
                          {template.platform && (
                            <span className="text-[10px] text-[var(--text-muted)]">· {template.platform}</span>
                          )}
                          {template.use_count > 0 && (
                            <span className="text-[10px] text-[var(--text-muted)]">· Used {template.use_count}x</span>
                          )}
                        </div>
                        {template.description && (
                          <p className="text-xs text-[var(--text-muted)] truncate mt-0.5">{template.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-1 ml-2">
                        <span className="text-xs text-[#FF4400] opacity-0 group-hover:opacity-100 transition-opacity font-['JetBrains_Mono']">
                          LOAD
                        </span>
                        <button
                          type="button"
                          onClick={(e) => handleDelete(template.template_id, e)}
                          className="p-1 rounded text-[var(--text-muted)] hover:text-red-400 transition-all"
                          data-testid={`delete-template-${template.template_id}`}
                        >
                          <Trash size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
